import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTest } from '../context/TestContext';
import { 
  FaCamera, 
  FaMicrophone, 
  FaBatteryFull, 
  FaWifi, 
  FaVolumeUp,
  FaPlay,
  FaCog
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const WelcomeContainer = styled.div`
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #ffffff, #e0e7ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 18px;
  opacity: 0.8;
  margin-bottom: 20px;
`;

const UrgencyBadge = styled(motion.div)`
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  max-width: 1200px;
  width: 100%;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SystemCheckItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const CheckInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CheckIcon = styled.div`
  font-size: 18px;
  color: ${props => props.status ? '#10b981' : props.status === false ? '#ef4444' : '#f59e0b'};
`;

const CheckText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const StatusIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.status ? '#10b981' : props.status === false ? '#ef4444' : '#f59e0b'};
  box-shadow: 0 0 10px ${props => props.status ? '#10b981' : props.status === false ? '#ef4444' : '#f59e0b'};
`;

const CameraPreview = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #1f2937, #374151);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
`;

const CameraOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 160px;
  border: 2px dashed rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CameraIcon = styled.div`
  font-size: 48px;
  opacity: 0.3;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  max-width: 400px;
`;

const Button = styled(motion.button)`
  padding: 16px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
  width: 100%;
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  `}
`;

const LanguageSelector = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const LanguageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const WelcomeScreen = ({ onStart }) => {
  const { systemChecks, actions } = useTest();
  const [isAllChecksPassed, setIsAllChecksPassed] = useState(false);

  useEffect(() => {
    const allPassed = Object.values(systemChecks).every(check => check === true);
    setIsAllChecksPassed(allPassed);
  }, [systemChecks]);

  const handleStartTest = () => {
    if (isAllChecksPassed) {
      toast.success('Test démarré avec succès !');
      onStart();
    } else {
      toast.error('Veuillez corriger les problèmes avant de commencer');
    }
  };

  const handleVolumeTest = () => {
    toast.success('Test audio effectué');
  };

  const handleLanguageChange = (lang) => {
    actions.setLanguage(lang);
    toast.success(`Langue changée vers ${lang === 'fr' ? 'Français' : 'English'}`);
  };

  const systemChecksList = [
    { key: 'camera', label: 'Caméra', icon: FaCamera },
    { key: 'microphone', label: 'Microphone', icon: FaMicrophone },
    { key: 'battery', label: 'Batterie', icon: FaBatteryFull },
    { key: 'storage', label: 'Espace disque', icon: FaCog },
    { key: 'internet', label: 'Connexion Internet', icon: FaWifi },
    { key: 'environment', label: 'Environnement', icon: FaCog },
  ];

  return (
    <WelcomeContainer>
      <Header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Title>Exam Video Recorder</Title>
        <Subtitle>Dispositif médical pour l'évaluation des AVC</Subtitle>
        <UrgencyBadge
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          ⏰ Time is Brain
        </UrgencyBadge>
      </Header>

      <MainContent>
        <Card
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <CardTitle>
            <FaCog />
            Vérifications système
          </CardTitle>
          {systemChecksList.map((check, index) => (
            <SystemCheckItem
              key={check.key}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
            >
              <CheckInfo>
                <CheckIcon status={systemChecks[check.key]}>
                  <check.icon />
                </CheckIcon>
                <CheckText>{check.label}</CheckText>
              </CheckInfo>
              <StatusIndicator status={systemChecks[check.key]} />
            </SystemCheckItem>
          ))}
        </Card>

        <Card
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <CardTitle>
            <FaCamera />
            Aperçu caméra
          </CardTitle>
          <CameraPreview>
            <CameraOverlay>
              <CameraIcon>👤</CameraIcon>
            </CameraOverlay>
          </CameraPreview>
          <Button variant="secondary" onClick={handleVolumeTest}>
            <FaVolumeUp />
            Tester le volume
          </Button>
        </Card>
      </MainContent>

      <ActionButtons
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <LanguageSelector>
          <LanguageButton 
            active={true} 
            onClick={() => handleLanguageChange('fr')}
          >
            🇫🇷 Français
          </LanguageButton>
          <LanguageButton 
            active={false} 
            onClick={() => handleLanguageChange('en')}
          >
            🇬🇧 English
          </LanguageButton>
        </LanguageSelector>

        <Button 
          variant="primary" 
          onClick={handleStartTest}
          disabled={!isAllChecksPassed}
          whileHover={{ scale: isAllChecksPassed ? 1.02 : 1 }}
          whileTap={{ scale: isAllChecksPassed ? 0.98 : 1 }}
        >
          <FaPlay />
          {isAllChecksPassed ? 'Commencer le test AVC' : 'Vérifications en cours...'}
        </Button>

        {!isAllChecksPassed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ textAlign: 'center', fontSize: '14px', opacity: 0.8 }}
          >
            ⚠️ Toutes les vérifications doivent être validées
          </motion.div>
        )}
      </ActionButtons>
    </WelcomeContainer>
  );
};

export default WelcomeScreen; 